import {
  CUSTOMER_PRIORITY_CHANNELS,
  type CustomerPriorityChannel,
} from "../../../../../_shared/_logic/enums"

export type { CustomerPriorityChannel } from "../../../../../_shared/_logic/enums"
export type ChannelPriorityInput =
  | Partial<Record<CustomerPriorityChannel, number | string>>
  | null
  | undefined
export type ChannelPriorityMap = Record<CustomerPriorityChannel, number>

export const PRIORITY_CHANNELS = CUSTOMER_PRIORITY_CHANNELS

const CHANNEL_ORDER_INDEX = Object.fromEntries(
  PRIORITY_CHANNELS.map((channel, idx) => [channel, idx]),
) as Record<CustomerPriorityChannel, number>

export const priorityChannelLabels: Record<CustomerPriorityChannel, string> = {
  EMAIL: "E-Mail",
  MOBILE_MESSAGE: "Mobile Nachricht",
  PHONE: "Telefon",
  LETTER: "Brief",
}

export const defaultChannelPriorities = (): ChannelPriorityMap =>
  PRIORITY_CHANNELS.reduce((acc, channel, idx) => {
    acc[channel] = idx + 1

    return acc
  }, {} as ChannelPriorityMap)

export const normalizeChannelPriorities = (
  priorities?: ChannelPriorityInput,
): ChannelPriorityMap => {
  if (!priorities) return defaultChannelPriorities()

  const ranked = PRIORITY_CHANNELS.flatMap((channel) => {
    const raw = priorities[channel]

    if (raw === undefined || raw === null) return []

    const parsed = typeof raw === "number" ? raw : parseInt(String(raw), 10)
    const priority = Number.isFinite(parsed) && parsed > 0 ? parsed : 1

    return [[channel, priority] as const]
  })

  ranked.sort((a, b) => {
    if (a[1] === b[1]) return CHANNEL_ORDER_INDEX[a[0]] - CHANNEL_ORDER_INDEX[b[0]]

    return a[1] - b[1]
  })

  const orderedChannels: CustomerPriorityChannel[] = ranked.map(([channel]) => channel)
  PRIORITY_CHANNELS.forEach((channel) => {
    if (!orderedChannels.includes(channel)) orderedChannels.push(channel)
  })

  return orderedChannels.reduce((acc, channel, idx) => {
    acc[channel] = idx + 1

    return acc
  }, {} as ChannelPriorityMap)
}

export const orderChannelsByPriority = (
  priorities?: ChannelPriorityInput,
): CustomerPriorityChannel[] => {
  const normalized = normalizeChannelPriorities(priorities)

  return [...PRIORITY_CHANNELS].sort((a, b) => {
    const diff = normalized[a] - normalized[b]

    if (diff !== 0) return diff

    return CHANNEL_ORDER_INDEX[a] - CHANNEL_ORDER_INDEX[b]
  })
}

export const formatChannelPriorityList = (priorities?: ChannelPriorityInput): string =>
  orderChannelsByPriority(priorities)
    .map((channel) => priorityChannelLabels[channel])
    .join(" → ")
